import React, { useEffect, useMemo, useState } from 'react';
import { aiAgentApi } from '../../../lib/aiAgentApi';
import { toast } from 'sonner';
import { Mail, Send, Paperclip, X, Trash2, CheckCircle2, XCircle, RotateCcw, Inbox, Edit3, ClipboardCheck, AlertCircle, CheckCheck, RefreshCw } from 'lucide-react';
import { RichTextEditor } from './RichTextEditor';

const TABS = [
  { id: 'inbox', label: 'Inbox', Icon: Inbox },
  { id: 'sent', label: 'Sent', Icon: Send },
  { id: 'approvals', label: 'Waiting for Approvals', Icon: ClipboardCheck },
  { id: 'compose', label: 'Compose', Icon: Edit3 },
];

const fmt = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const StatusPill = ({ status }) => {
  const map = {
    sent: ['bg-emerald-100 text-emerald-700', CheckCircle2],
    pending_approval: ['bg-amber-100 text-amber-700', ClipboardCheck],
    queued: ['bg-sky-100 text-sky-700', Send],
    sending: ['bg-sky-100 text-sky-700', Send],
    failed: ['bg-rose-100 text-rose-700', AlertCircle],
    draft: ['bg-slate-100 text-slate-600', Edit3],
    received: ['bg-violet-100 text-violet-700', Inbox],
  };
  const [cls, Icon] = map[status] || ['bg-slate-100 text-slate-600', Mail];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}>
      <Icon className="h-3 w-3" /> {status}
    </span>
  );
};

const ApprovalsToolbar = ({ emails, onChanged }) => {
  const [busy, setBusy] = useState(false);
  // Group pending emails by batch_id so we can offer "Approve this batch"
  const groups = useMemo(() => {
    const map = new Map();
    for (const e of emails) {
      const key = e.batch_id || '__manual__';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }
    return Array.from(map.entries()).map(([batch_id, items]) => ({ batch_id, items }));
  }, [emails]);

  const approveAll = async (batch_id = null) => {
    const items = batch_id ? groups.find(g => g.batch_id === batch_id)?.items || [] : emails;
    if (!items.length) return;
    if (!window.confirm(`Approve and queue ${items.length} email${items.length === 1 ? '' : 's'} for delivery?`)) return;
    setBusy(true);
    try {
      if (batch_id && batch_id !== '__manual__') {
        await aiAgentApi.approveBatch({ batch_id });
      } else {
        await aiAgentApi.approveBatch({ email_ids: items.map(e => e.id) });
      }
      toast.success(`Approved ${items.length} email${items.length === 1 ? '' : 's'}`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk approve failed');
    } finally { setBusy(false); }
  };

  const hasBatches = groups.some(g => g.batch_id && g.batch_id !== '__manual__');

  return (
    <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5" data-testid="ai-email-approvals-toolbar">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-amber-900">
            {emails.length} email{emails.length === 1 ? '' : 's'} waiting for your approval
          </div>
          {hasBatches && (
            <div className="mt-0.5 text-xs text-amber-700">
              {groups.filter(g => g.batch_id !== '__manual__').length} agent batch{groups.filter(g => g.batch_id !== '__manual__').length === 1 ? '' : 'es'} ready to send
            </div>
          )}
        </div>
        <button
          data-testid="ai-email-approve-all"
          onClick={() => approveAll(null)}
          disabled={busy}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-40"
        >
          <CheckCheck className="h-3.5 w-3.5" /> {busy ? 'Approving…' : `Approve all (${emails.length})`}
        </button>
      </div>
      {hasBatches && groups.filter(g => g.batch_id !== '__manual__').length > 1 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {groups.filter(g => g.batch_id !== '__manual__').map((g) => (
            <button
              key={g.batch_id}
              data-testid={`ai-email-approve-batch-${g.batch_id}`}
              onClick={() => approveAll(g.batch_id)}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-amber-900 hover:bg-amber-100"
            >
              <CheckCheck className="h-3 w-3" /> Approve batch · {g.items.length} email{g.items.length === 1 ? '' : 's'}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


const EmailRow = ({ email, onOpen }) => {
  const dest = (email.to || []).join(', ') || email.user_email || '—';
  const preview = (email.body_text || '').replace(/\s+/g, ' ').slice(0, 140);
  return (
    <li
      data-testid={`ai-email-row-${email.id}`}
      onClick={() => onOpen(email)}
      className="cursor-pointer rounded-xl border border-slate-200 bg-white px-4 py-3 transition hover:border-emerald-200 hover:shadow-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="truncate text-sm font-semibold text-slate-900">{email.subject || '(no subject)'}</div>
            {email.ai_drafted && <span className="rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">AI drafted</span>}
          </div>
          <div className="mt-0.5 truncate text-xs text-slate-500">{email.direction === 'inbound' ? `From: ${email.reply_to || '—'}` : `To: ${dest}`}</div>
          {preview && <div className="mt-0.5 truncate text-xs text-slate-400">{preview}</div>}
        </div>
        <div className="flex flex-col items-end gap-1">
          <StatusPill status={email.status} />
          <div className="text-[10px] text-slate-400">{fmt(email.updated_at || email.created_at)}</div>
        </div>
      </div>
    </li>
  );
};

const EmailDetail = ({ email, onClose, onChanged }) => {
  const [busy, setBusy] = useState(false);

  // ── Edit mode (only available while pending_approval) ──
  const [editing, setEditing] = useState(false);
  const [editTo, setEditTo] = useState('');
  const [editCc, setEditCc] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [editBodyHtml, setEditBodyHtml] = useState('');

  const startEdit = async () => {
    // The list endpoint trims body_html to save bytes; fetch the full
    // record so the rich-text editor opens with the actual draft content.
    let full = email;
    if (!email.body_html) {
      try {
        full = await aiAgentApi.getEmail(email.id);
      } catch {
        full = email;
      }
    }
    setEditTo((full.to || []).join(', '));
    setEditCc((full.cc || []).join(', '));
    setEditSubject(full.subject || '');
    setEditBodyHtml(full.body_html || full.body_text || '');
    setEditing(true);
  };
  const cancelEdit = () => setEditing(false);

  const addSelfToCc = (myEmail) => {
    if (!myEmail) return;
    const ccList = editCc.split(',').map(s => s.trim()).filter(Boolean);
    if (ccList.map(s => s.toLowerCase()).includes(myEmail.toLowerCase())) {
      toast.info('Already on CC');
      return;
    }
    setEditCc([...ccList, myEmail].join(', '));
  };

  const saveEdit = async (alsoApprove = false) => {
    const toList = editTo.split(',').map(s => s.trim()).filter(Boolean);
    const ccList = editCc.split(',').map(s => s.trim()).filter(Boolean);
    if (toList.length === 0) { toast.error('Add at least one recipient'); return; }
    if (!editSubject.trim()) { toast.error('Subject is required'); return; }
    setBusy(true);
    try {
      await aiAgentApi.editEmail(email.id, {
        to: toList, cc: ccList,
        subject: editSubject.trim(),
        body_html: editBodyHtml,
      });
      if (alsoApprove) {
        await aiAgentApi.approveEmail(email.id);
        toast.success('Edits saved · approved & queued');
        onChanged?.(); onClose();
      } else {
        toast.success('Edits saved');
        setEditing(false);
        onChanged?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save edits');
    } finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await aiAgentApi.approveEmail(email.id);
      toast.success('Approved — queued to send');
      onChanged?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    const reason = window.prompt('Reject reason (optional):', '');
    if (reason === null) return;
    setBusy(true);
    try {
      await aiAgentApi.rejectEmail(email.id, reason);
      toast.success('Rejected');
      onChanged?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reject failed'); }
    finally { setBusy(false); }
  };

  const retry = async () => {
    setBusy(true);
    try {
      await aiAgentApi.retryEmail(email.id);
      toast.success('Re-queued');
      onChanged?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Retry failed'); }
    finally { setBusy(false); }
  };

  // Owner email (used by "CC me" shortcut). Stored once at login in localStorage.auth.
  let ownerEmail = '';
  try {
    ownerEmail = JSON.parse(localStorage.getItem('auth') || '{}')?.user?.email || '';
  } catch { /* ignore */ }

  return (
    <div className="flex h-full flex-col" data-testid="ai-email-detail">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
        <button onClick={onClose} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        <div className="flex items-center gap-2">
          {email.status === 'pending_approval' && !editing && (
            <>
              <button
                data-testid="ai-email-edit"
                onClick={startEdit}
                disabled={busy}
                className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"
              >
                <Edit3 className="h-3 w-3" /> Edit
              </button>
              <button data-testid="ai-email-reject" onClick={reject} disabled={busy} className="rounded-md border border-rose-200 px-2.5 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50">Reject</button>
              <button data-testid="ai-email-approve" onClick={approve} disabled={busy} className="rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700">Approve &amp; Send</button>
            </>
          )}
          {email.status === 'pending_approval' && editing && (
            <>
              <button
                data-testid="ai-email-cancel-edit"
                onClick={cancelEdit}
                disabled={busy}
                className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >Cancel</button>
              <button
                data-testid="ai-email-save-edit"
                onClick={() => saveEdit(false)}
                disabled={busy}
                className="rounded-md border border-emerald-200 px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
              >{busy ? 'Saving…' : 'Save changes'}</button>
              <button
                data-testid="ai-email-save-and-approve"
                onClick={() => saveEdit(true)}
                disabled={busy}
                className="rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700"
              >{busy ? 'Saving…' : 'Save & Send'}</button>
            </>
          )}
          {email.status === 'failed' && (
            <button data-testid="ai-email-retry" onClick={retry} disabled={busy} className="rounded-md bg-sky-600 px-3 py-1 text-xs font-semibold text-white hover:bg-sky-700"><RotateCcw className="mr-1 inline h-3 w-3" />Retry</button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {editing ? (
          <div className="space-y-2.5" data-testid="ai-email-edit-form">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">To</label>
              <input
                data-testid="ai-email-edit-to"
                value={editTo}
                onChange={(e) => setEditTo(e.target.value)}
                placeholder="vendor@example.com"
                className="mt-0.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Cc</label>
                {ownerEmail && (
                  <button
                    type="button"
                    onClick={() => addSelfToCc(ownerEmail)}
                    className="text-[10px] font-semibold text-emerald-700 hover:underline"
                    data-testid="ai-email-cc-me"
                  >+ CC me ({ownerEmail})</button>
                )}
              </div>
              <input
                data-testid="ai-email-edit-cc"
                value={editCc}
                onChange={(e) => setEditCc(e.target.value)}
                placeholder="(optional — comma separated)"
                className="mt-0.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Subject</label>
              <input
                data-testid="ai-email-edit-subject"
                value={editSubject}
                onChange={(e) => setEditSubject(e.target.value)}
                className="mt-0.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Message</label>
              <div className="mt-0.5" data-testid="ai-email-edit-body">
                <RichTextEditor value={editBodyHtml} onChange={setEditBodyHtml} minHeight={240} />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="text-xs text-slate-500">{email.direction === 'outbound' ? 'To' : 'From'}</div>
            <div className="text-sm text-slate-900">{email.direction === 'outbound' ? (email.to || []).join(', ') : email.reply_to}</div>
            {email.cc?.length > 0 && <><div className="mt-2 text-xs text-slate-500">Cc</div><div className="text-sm text-slate-900">{email.cc.join(', ')}</div></>}
            <div className="mt-3 text-lg font-semibold text-slate-900">{email.subject || '(no subject)'}</div>
            <div className="mt-2"><StatusPill status={email.status} /> {email.ai_drafted && <span className="ml-2 rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">AI drafted</span>}</div>
            <div className="mt-4 rounded-xl border border-slate-100 bg-white p-4">
              {email.body_html ? (
                <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: email.body_html }} />
              ) : (
                <pre className="whitespace-pre-wrap text-sm text-slate-700">{email.body_text}</pre>
              )}
            </div>
            {email.error && (
              <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                <div className="font-semibold">Error</div>
                <div>{email.error}</div>
              </div>
            )}
            {email.attachments?.length > 0 && (
              <div className="mt-3">
                <div className="text-xs font-semibold text-slate-500">Attachments</div>
                <ul className="mt-1 space-y-1">
                  {email.attachments.map((a, idx) => (
                    <li key={idx} className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-1 text-[11px] text-slate-700">
                      <Paperclip className="h-3 w-3" />{a.filename}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const Composer = ({ convId, onSent }) => {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);

  const onAttach = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('File exceeds 25MB'); return; }
    try {
      const pre = await aiAgentApi.presignUpload({
        filename: file.name, content_type: file.type || 'application/octet-stream',
        size_bytes: file.size, conv_id: convId, visibility: 'private',
      });
      const r = await fetch(pre.upload_url, { method: 'PUT', headers: { 'Content-Type': pre.content_type }, body: file });
      if (!r.ok) throw new Error(`Upload failed (${r.status})`);
      const confirmed = await aiAgentApi.confirmUpload({
        file_id: pre.file_id, s3_key: pre.s3_key, filename: file.name,
        content_type: file.type || 'application/octet-stream',
        size_bytes: file.size, conv_id: convId, visibility: 'private',
      });
      setAttachments((prev) => [...prev, confirmed]);
    } catch (e) { toast.error(e?.message || 'Upload failed'); }
  };

  const send = async () => {
    const toList = to.split(',').map(s => s.trim()).filter(Boolean);
    const ccList = cc.split(',').map(s => s.trim()).filter(Boolean);
    if (toList.length === 0) { toast.error('Add at least one recipient'); return; }
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    setBusy(true);
    try {
      await aiAgentApi.composeEmail({
        to: toList, cc: ccList, subject: subject.trim(), body_html: bodyHtml,
        conv_id: convId || null, send_now: true,
        attachments: attachments.map(a => ({
          file_id: a.id, s3_key: a.s3_key, filename: a.filename,
          content_type: a.content_type, size_bytes: a.size_bytes,
        })),
      });
      toast.success('Queued for delivery');
      setTo(''); setCc(''); setSubject(''); setBodyHtml(''); setAttachments([]);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send');
    } finally { setBusy(false); }
  };

  return (
    <div className="flex h-full flex-col" data-testid="ai-email-composer">
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">To</label>
          <input
            data-testid="ai-email-to"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="vendor@example.com, ops@example.com"
            className="mt-0.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
          />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Cc</label>
          <input
            data-testid="ai-email-cc"
            value={cc}
            onChange={(e) => setCc(e.target.value)}
            placeholder="(optional)"
            className="mt-0.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
          />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Subject</label>
          <input
            data-testid="ai-email-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Re: Account 4827 — payment extension request"
            className="mt-0.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
          />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Message</label>
          <div className="mt-0.5">
            <RichTextEditor value={bodyHtml} onChange={setBodyHtml} />
          </div>
        </div>
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-2">
            {attachments.map((a) => (
              <span key={a.id} className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700">
                <Paperclip className="h-3 w-3" />{a.filename}
                <button onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))} className="ml-0.5 text-slate-400 hover:text-rose-600">×</button>
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between border-t border-slate-100 bg-white px-4 py-3">
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-emerald-300">
          <Paperclip className="h-3.5 w-3.5" /> Attach (≤25MB)
          <input type="file" className="hidden" onChange={(e) => { onAttach(e.target.files?.[0]); e.target.value = ''; }} />
        </label>
        <button
          data-testid="ai-email-send"
          onClick={send}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-40"
        >
          <Send className="h-3.5 w-3.5" /> {busy ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
};

export const EmailView = ({ initialTab }) => {
  const [tab, setTab] = useState(initialTab || 'inbox');

  // Honour external tab changes (e.g. when an in-chat "Open Approvals" link
  // is clicked the drawer routes us to Email → Approvals).
  useEffect(() => {
    if (initialTab) setTab(initialTab);
  }, [initialTab]);
  const [emails, setEmails] = useState([]);
  const [counts, setCounts] = useState({});
  const [active, setActive] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await aiAgentApi.listEmails(tab === 'compose' ? 'all' : tab);
        if (cancelled) return;
        setEmails(r.emails || []);
        setCounts(r.counts || {});
      } catch { toast.error('Could not load emails'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [tab, refreshKey]);

  return (
    <div className="flex h-full flex-col">
      {/* Tabs */}
      <div className="flex items-center border-b border-slate-100 px-1">
        <div className="flex flex-1 items-center">
          {TABS.map(({ id, label, Icon }) => {
            const c = counts[id];
            const showBadge = c && id !== 'compose';
            return (
              <button
                key={id}
                data-testid={`ai-email-tab-${id}`}
                onClick={() => { setTab(id); setActive(null); }}
                className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-[12px] font-semibold transition ${tab === id ? 'border-emerald-500 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-900'}`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
                {showBadge ? <span className="rounded-full bg-slate-100 px-1.5 text-[10px] text-slate-600">{c}</span> : null}
              </button>
            );
          })}
        </div>
        <button
          data-testid="ai-email-refresh"
          onClick={() => { setActive(null); setRefreshKey((k) => k + 1); }}
          title="Refresh inbox"
          disabled={loading}
          className="mr-1 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-40"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="min-h-0 flex-1">
        {active ? (
          <EmailDetail email={active} onClose={() => setActive(null)} onChanged={() => setRefreshKey((k) => k + 1)} />
        ) : tab === 'compose' ? (
          <Composer onSent={() => { setTab('sent'); setRefreshKey((k) => k + 1); }} />
        ) : (
          <div className="h-full overflow-y-auto px-3 py-3">
            {loading && <div className="py-8 text-center text-xs text-slate-400">Loading…</div>}
            {!loading && emails.length === 0 && (
              <div className="mt-10 text-center">
                <Mail className="mx-auto h-8 w-8 text-slate-300" />
                <div className="mt-2 text-sm font-semibold text-slate-900">Nothing here</div>
                <div className="mt-0.5 text-xs text-slate-500">
                  {tab === 'inbox' && 'Inbound replies will land here once your Mailgun route is set up.'}
                  {tab === 'sent' && 'Sent emails will appear here once you approve and send them.'}
                  {tab === 'approvals' && 'AI-drafted emails awaiting your approval will appear here.'}
                </div>
              </div>
            )}
            <ul className="space-y-2">
              {emails.map((e) => <EmailRow key={e.id} email={e} onOpen={setActive} />)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
